/**
 * DeviceProfilePage — Unified V2 orchestrator for Inovonics + Palcare device profiles.
 *
 * Manages:
 *   - Session & permissions
 *   - Two polling cycles (basic data 600s, reporting data 1200s)
 *   - Tab state (basic, reporting, wellness, logs, debug, find)
 *   - Drawer/dialog state for all edit flows
 *   - Toast notifications
 *   - Conditional rendering based on manufacture_id (1=Inovonics, 4=Palcare)
 *
 * Props:
 *   id    — Device ID (from URL param)
 *   mfgId — Manufacture ID ("1" for Inovonics, "4" for Palcare)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './device-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import ReportingTab from './components/ReportingTab.jsx';
import WellnessTab from './components/WellnessTab.jsx';
import DeviceLogsTab from './components/DeviceLogsTab.jsx';
import TroubleshootTab from './components/TroubleshootTab.jsx';
import FindMyDeviceTab from './components/FindMyDeviceTab.jsx';
import ConfirmTypedDialog from '../../../components/v2/ConfirmTypedDialog.jsx';
import { describeDeviceAction } from '../components/deviceActionCopy.js';
import EventNoteDialog from './components/EventNoteDialog.jsx';
import MuteDeviceDialog from './components/MuteDeviceDialog.jsx';
import ActiveAlertPanel from '../../../components/v2/ActiveAlertPanel.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, updateSessionCampus, getRoleWeight, getActiveVendorIdList, getCampusConfig,
} from '../../../services/session.js';
import {
  fetchInovonicsProfile, fetchPalcareProfile,
  fetchAreas, fetchResidents,
  fetchCareflowWindows, fetchDeviceLogs, fetchDeviceUseMap,
  fetchPreformNotes, fetchDspProfiles,
  fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
  fetchDebugData, fetchDeviceLocation, sendRemoteCommand,
  editDeviceBasicInfo, unassignDevice, addEventNote, startDeviceLogging,
  tempMuteDevice,
} from './api/deviceProfileApi.js';
import { fetchCallLogsReport } from '../../reports/call-logs/api/callLogsReportApi.js';
import { isMuted, muteUntilTimestamp } from '../../../utils/deviceMute.js';
import { MUTED_BADGE, showsMuted } from '../../../utils/deviceStatus.js';
import { alertsForDevice } from '../../../utils/activeAlerts.js';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
  iAmHigherAuthority as iAmHigherAuth,
  convertIsoToLocal,
  /* away_until is stored UTC, unlike last_alarm / last_active which the procedure
     already returns campus-local — so it needs the offset-aware formatter. */
  convertIsoUTCToLocal,
  convertIsoUTCToLocalized,
  formatDateIso,
  shiftDateStr,
  getToday,
  getUTCTimestamp,
} from '../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const DeviceProfilePage = ({ id, mfgId }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // DEVICE TYPE
  // --------------------------------------------------------------------------
  const isPalcare = useMemo(() => String(mfgId) === '4', [mfgId]);
  const isInovonics = useMemo(() => String(mfgId) === '1', [mfgId]);

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId.toString()]?.name || s?.campus || '';
  }, [campusId]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const showAdminTabs = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  const deviceId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // Campus config for the active campus — the one canonical accessor for all
  // campus-specific settings (feature flags, timezone, coordinates) used here.
  const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
  const eventNotesActive = campusConfig?.event_notes_active || false;
  const eventNotesPreform = campusConfig?.event_notes_preform || false;
  const eventNotesFreeform = campusConfig?.event_notes_freeform || false;
  const isReasonEditable = campusConfig?.is_reason_editable || false;
  // Grandstream campuses get a Calls column (call logs merged into events by event_id).
  const isGrandstream = Boolean(campusConfig?.is_grandstream);
  const tzOffset = parseInt(campusConfig?.tz_offset || 0);

  // Campus coordinates (for Palcare maps)
  const campusLat = useMemo(() => parseFloat(campusConfig?.latitude) || 0, [campusConfig]);
  const campusLong = useMemo(() => parseFloat(campusConfig?.longitude) || 0, [campusConfig]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // TABS
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('basic');

  // --------------------------------------------------------------------------
  // CORE STATE
  // --------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);

  // Basic device data
  const [basicData, setBasicData] = useState({});
  const [deviceName, setDeviceName] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [description, setDescription] = useState('');
  const [deviceTypeId, setDeviceTypeId] = useState(0);
  const [deviceTypeName, setDeviceTypeName] = useState('');
  const [deviceCategory, setDeviceCategory] = useState('');
  const [isAreaDevice, setIsAreaDevice] = useState(false);
  const [areaId, setAreaId] = useState(0);
  const [areaName, setAreaName] = useState('');
  const [residentId, setResidentId] = useState(0);
  const [residentName, setResidentName] = useState('');
  const [isActive, setIsActive] = useState(0);
  const [deviceImg, setDeviceImg] = useState('');
  const [modelName, setModelName] = useState('');
  const [lastRepeater, setLastRepeater] = useState('');
  const [lastAlarm, setLastAlarm] = useState('');
  const [lastActive, setLastActive] = useState('');
  const [areaGroups, setAreaGroups] = useState([]);
  const [resGroups, setResGroups] = useState([]);

  // Inovonics-specific state
  const [deviceUseId, setDeviceUseId] = useState(0);
  const [behaviorText, setBehaviorText] = useState('');
  const [currentTemp, setCurrentTemp] = useState('');
  const [currentHumidity, setCurrentHumidity] = useState('');
  const [currentBattery, setCurrentBattery] = useState('');
  const [useMapData, setUseMapData] = useState([]);
  const [debugData, setDebugData] = useState([]);

  // Palcare-specific state
  const [dspProfileId, setDspProfileId] = useState(0);
  const [dspProfileName, setDspProfileName] = useState('');
  const [dspProfiles, setDspProfiles] = useState([]);

  // Shared dropdowns
  const [areas, setAreas] = useState([]);
  const [residents, setResidents] = useState([]);
  const [prenoteData, setPrenoteData] = useState([]);

  // Reporting data
  const [hourData, setHourData] = useState({});
  const [weekData, setWeekData] = useState({});
  const [reasonData, setReasonData] = useState({});
  const [eventData, setEventData] = useState([]);

  // Wellness data
  const [careData, setCareData] = useState([]);

  // Log data
  const [logData, setLogData] = useState([]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteEventIndex, setNoteEventIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  /* Temporary alarm suppression. `awayUntil` is the raw UTC string from
     core_device.away_until; `muting` brackets the write like `saving` does
     elsewhere. `muteTick` exists only to re-run the isMuted() clock comparison —
     see the interval below. */
  const [awayUntil, setAwayUntil] = useState('');
  const [muting, setMuting] = useState(false);
  const [muteDialogOpen, setMuteDialogOpen] = useState(false);

  /* Live alerts for this device, from the campus-wide provider that already backs
     both alerts pages — no fetch or polling of our own. Exactly one array is ever
     populated (cloud on 5.0, legacy on 4.0), so both are handed over and
     alertsForDevice sorts out which applies.

     Deliberately NOT calling registerAlertViewer(): that switches on a campus-wide
     reconciliation fetch every 60s, which is for pages showing the whole alert
     list. This page rides the subscription push instead, and the provider's
     stale-detection resubscribe still covers drift. */
  const { activeCloudAlarms, activeAlarms } = useNotifications();
  /* Derived here, immediately below the provider and above every action handler,
     because those handlers list alertActive in their useCallback dependency
     arrays. A dep array is built when the useCallback line runs, so a const
     declared further down would throw a ReferenceError on the first render. */
  const deviceAlerts = useMemo(
    () => alertsForDevice(activeCloudAlarms, activeAlarms, deviceId),
    [activeCloudAlarms, activeAlarms, deviceId],
  );

  /* Blocks Suppress, Edit and Unassign while any alert is live: all three can stop
     the device's clear signal being processed. */
  const alertActive = deviceAlerts.length > 0;

  const [muteTick, setMuteTick] = useState(0);

  // --------------------------------------------------------------------------
  // DATE RANGE (for event reason summary)
  // --------------------------------------------------------------------------
  const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
  const endDate = useMemo(() => formatDateIso(getToday()), []);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 1: Basic data (600 second interval)
  // --------------------------------------------------------------------------
  const fetchBasicDataCycle = useCallback(async () => {
    // Build parallel fetch list — profile API differs by device type
    const profileFetch = isPalcare
      ? fetchPalcareProfile(campusId, deviceId)
      : fetchInovonicsProfile(campusId, deviceId);

    const baseFetches = [
      profileFetch,
      fetchAreas(campusId),
      fetchResidents(campusId),
      fetchCareflowWindows(campusId),
    ];

    // Device logs for both types when admin
    if (showAdminTabs) {
      baseFetches.push(fetchDeviceLogs(deviceId));
    } else {
      baseFetches.push(Promise.resolve([]));
    }

    // Palcare: also fetch DSP profiles
    if (isPalcare) {
      baseFetches.push(fetchDspProfiles(campusId));
    } else {
      baseFetches.push(Promise.resolve([]));
    }

    const [profileResult, areasResult, residentsResult, careResult, logsResult, extraResult] =
      await Promise.all(baseFetches);

    // Process profile data (shared fields)
    if (profileResult?.length > 0) {
      const b = profileResult[0];
      setBasicData(b);
      setDeviceName(b.device_name || '');
      setSerialNum(b.manufacture_uuid || '');
      setDescription(b.descriptions || '');
      setDeviceTypeId(b.device_type_id || 0);
      setDeviceTypeName(b.device_type_name || '');
      setDeviceCategory(b.device_category || b.device_category_short_name || '');
      setIsAreaDevice(b.device_type === 'location' || b.is_area_device === 1);
      setAreaId(b.area_id || 0);
      setAreaName(b.area_name || '');
      setResidentId(b.resident_id || 0);
      setResidentName(b.resident_name || '');
      /* Number(), not raw truthiness: is_active is a MySQL tinyint and the string
         "0" is truthy in JS, which would show an unassigned device as Active and
         leave the Unassign button on it. Normalized once here so every consumer
         downstream can treat it as 1/0. */
      setIsActive(Number(b.is_active) ? 1 : 0);
      setDeviceImg(b.image_url || '');
      setModelName(b.inovonics_model_name || b.model_name || '');
      setLastRepeater(b.last_repeater || '');
      setLastAlarm(b.last_alarm || '');
      setLastActive(b.last_active || '');
      setAreaGroups(b.area_groups || []);
      setResGroups(b.resident_groups || []);
      setAwayUntil(b.away_until || '');

      if (isInovonics) {
        // Inovonics-specific fields
        setDeviceUseId(b.device_use_id || 0);
        setBehaviorText(b.option_text || 'Standard');
        setCurrentTemp(b.current_temperature || '');
        setCurrentHumidity(b.current_humidity || '');
        setCurrentBattery(b.current_battery || '');

        // Fetch behavior options based on device type
        if (b.device_type_id) {
          const useMap = await fetchDeviceUseMap(b.device_type_id);
          setUseMapData(useMap || []);
        }

        // Fetch debug data if we have a serial number
        if (b.manufacture_uuid && showAdminTabs) {
          const debug = await fetchDebugData(campusId, b.manufacture_uuid);
          const parsed = (debug || []).map((item) => ({
            ...item,
            Parsed: typeof item.Parsed === 'string' ? JSON.parse(item.Parsed) : item.Parsed,
          }));
          setDebugData(parsed);
        }
      }

      if (isPalcare) {
        // Palcare-specific fields
        setDspProfileId(b.dsp_profile_id || 0);
        setDspProfileName(b.profile_name || '');
      }
    }
    setAreas(areasResult || []);
    setResidents(residentsResult || []);
    setCareData(careResult || []);
    setLogData(logsResult || []);

    // Store DSP profiles for Palcare
    if (isPalcare) {
      setDspProfiles(extraResult || []);
    }

    setLoading(false);
  }, [campusId, deviceId, showAdminTabs, isPalcare, isInovonics]);

  useEffect(() => {
    fetchBasicDataCycle();
    const interval = setInterval(fetchBasicDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchBasicDataCycle]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 2: Reporting data (1200 second interval)
  // --------------------------------------------------------------------------
  const fetchReportingCycle = useCallback(async () => {
    const [hourResult, weekResult, reasonResult, eventsResult, prenotes] = await Promise.all([
      fetchHourChart(campusId, deviceId),
      fetchWeekChart(campusId, deviceId),
      fetchReasonSummary(campusId, deviceId, startDate, endDate),
      fetchLimitedEvents(campusId, deviceId),
      (eventNotesPreform ? fetchPreformNotes(campusId) : Promise.resolve([])),
    ]);

    let events = eventsResult || [];

    // Grandstream campuses: fetch call logs for the events' date range and merge
    // them into events by event_id (mirrors AlertDetailsPage) so the Recent Events
    // table can show a per-event call count + hover. Non-fatal on failure.
    if (isGrandstream && events.length) {
      try {
        const dates = events
          .map((e) => (e.event_datetime_local ? String(e.event_datetime_local).slice(0, 10) : ''))
          .filter(Boolean)
          .sort();
        if (dates.length) {
          const callResp = await fetchCallLogsReport({
            campusId,
            startDate: shiftDateStr(dates[0], -1),
            endDate: shiftDateStr(dates[dates.length - 1], 1),
          });
          const callRecords = callResp?.records || [];
          if (callRecords.length) {
            const callsByEvent = new Map();
            callRecords.forEach((c) => {
              const key = String(c.event_id ?? '');
              if (!key) return;
              if (!callsByEvent.has(key)) callsByEvent.set(key, []);
              callsByEvent.get(key).push(c);
            });
            callsByEvent.forEach((arr) => arr.sort(
              (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
            ));
            events = events.map((r) => ({
              ...r,
              calls: callsByEvent.get(String(r.event_id ?? '')) || [],
            }));
          }
        }
      } catch (err) {
        console.error('[DeviceProfilePage] Call logs merge failed:', err);
      }
    }

    setHourData(hourResult || {});
    setWeekData(weekResult || {});
    setReasonData(reasonResult || {});
    setEventData(events);
    setPrenoteData(prenotes || []);
    setReportLoading(false);
  }, [campusId, deviceId, startDate, endDate, eventNotesPreform, isGrandstream]);

  useEffect(() => {
    fetchReportingCycle();
    const interval = setInterval(fetchReportingCycle, 1_200_000);
    return () => clearInterval(interval);
  }, [fetchReportingCycle]);

  // --------------------------------------------------------------------------
  // CAMPUS CHANGE HANDLER
  // --------------------------------------------------------------------------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    updateSessionCampus(val);
    navigate('/community/devices');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  /** Save basic info (description, assignment, behavior/profile, serial) */
  const handleBasicInfoSave = useCallback(async (formData) => {
    /* Defense-in-depth: Edit is already disabled while the device is alarming, but
       the alarm can start after the drawer is opened. This save can deactivate the
       device or move it to another area or resident, either of which can stop the
       clear signal being processed. */
    if (alertActive) {
      showToast('error', 'This device has an active alarm. Wait until the alert is resolved before editing it.');
      setBasicDrawerOpen(false);
      return;
    }
    setSaving(true);
    const vendor_ids = getActiveVendorIdList();
    const payload = [{
      device_id: deviceId,
      device_name: deviceName,
      descriptions: formData.description,
      is_active: formData.linkedId !== 0,
      area_id: isAreaDevice ? parseInt(formData.linkedId) : 0,
      resident_id: !isAreaDevice ? parseInt(formData.linkedId) : 0,
      manufacture_uuid: formData.serialNum.toString(),
      manufacture_id: mfgId != null ? parseInt(mfgId, 10) : null,
      modified_by: getUserId(),
      vendor_ids,
      // Inovonics: device_use_id; Palcare: dsp_profile_id
      ...(isInovonics && { device_use_id: parseInt(formData.deviceUseId || 0) }),
      ...(isPalcare && { dsp_profile_id: parseInt(formData.dspProfileId || 0) }),
    }];
    const oldPayload = [{
      device_id: deviceId,
      descriptions: description,
      is_active: isActive ? 1 : 0,
      area_id: areaId,
      resident_id: residentId,
      manufacture_uuid: serialNum,
      ...(isPalcare && { dsp_profile_id: dspProfileId }),
    }];

    const result = await editDeviceBasicInfo(payload, oldPayload, getSessionParams());
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Basic information saved.');
      setBasicDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [deviceId, deviceName, description, isActive, areaId, residentId, serialNum, isAreaDevice,
      isInovonics, isPalcare, dspProfileId, mfgId, getSessionParams, showToast, fetchBasicDataCycle, alertActive]);

  /* A mute expires by the clock passing it, not by anything arriving from the
     server, so nothing would re-render when it lapses. The basic-data cycle is a
     600s interval, which would leave an expired banner on screen for up to ten
     minutes. Ticking every 30s keeps the banner honest to within half a minute,
     and only while a mute is actually pending. */
  useEffect(() => {
    if (!awayUntil) return undefined;
    const id = setInterval(() => setMuteTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [awayUntil]);

  const deviceMuted = useMemo(
    // muteTick is a deliberate dependency: it is what re-evaluates the clock.
    () => isMuted(awayUntil),
    [awayUntil, muteTick],
  );

  /**
   * Suppress alarms for `minutes`, or resume them when `minutes` is null.
   *
   * One handler for both directions because both write the same column via the
   * same transType — an empty away_until is what "resume" means.
   */
  const handleMuteChange = useCallback(async (minutes) => {
    const nextAwayUntil = minutes == null ? '' : muteUntilTimestamp(minutes);
    if (minutes != null && !nextAwayUntil) {
      showToast('error', 'Could not work out the suppression window.');
      return;
    }

    setMuting(true);
    const payload = [{
      device_id: deviceId,
      away_until: nextAwayUntil,
      /* The same instant in campus-local time, pre-formatted for the device log
         entry the backend writes. Sent rather than derived server-side because
         tz_offset lives in the campus config the client already holds, and this
         formatter is the one every other local timestamp on the page goes through
         — so the log text cannot disagree with the banner above it.

         Empty when resuming: there is no "until" to state. */
      local_away_until: nextAwayUntil ? convertIsoUTCToLocal(nextAwayUntil, tzOffset) : '',
      modified_by: getUserId(),
      vendor_ids: getActiveVendorIdList(),
    }];
    /* Previous value so the audit diff shows what the mute replaced. */
    const oldPayload = [{ device_id: deviceId, away_until: awayUntil }];

    const result = await tempMuteDevice(payload, oldPayload, getSessionParams());
    setMuting(false);

    if (result && result[0]) {
      /* Reflect it locally so the banner appears without waiting on the refetch,
         then reconcile with the server. */
      setAwayUntil(nextAwayUntil);
      setMuteDialogOpen(false);
      showToast('success', nextAwayUntil ? 'Alarms suppressed.' : 'Alarms resumed.');
      fetchBasicDataCycle();
    } else {
      const verb = nextAwayUntil ? 'suppress alarms' : 'resume alarms';
      showToast('error', `Failed to ${verb}${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [deviceId, awayUntil, tzOffset, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Unassign and deactivate device */
  const handleUnassign = useCallback(async () => {
    /* Same guard as handleBasicInfoSave — the dialog can be open when an alarm
       starts, and unassigning deactivates the device mid-alarm. */
    if (alertActive) {
      showToast('error', 'This device has an active alarm. Wait until the alert is resolved before unassigning it.');
      setUnassignDialogOpen(false);
      return;
    }
    setSaving(true);
    const vendor_ids = getActiveVendorIdList();
    const payload = [{
      device_id: deviceId,
      device_name: deviceName,
      manufacture_id: mfgId != null ? parseInt(mfgId, 10) : null,
      is_active: false,
      resident_id: null,
      area_id: null,
      modified_by: getUserId(),
      vendor_ids,
    }];
    const oldPayload = [{
      device_id: deviceId,
      area_id: areaId,
      resident_id: residentId,
    }];

    const result = await unassignDevice(payload, oldPayload, getSessionParams());
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Device marked unassigned.');
      setUnassignDialogOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to unassign${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [deviceId, deviceName, mfgId, areaId, residentId, getSessionParams, showToast, fetchBasicDataCycle, alertActive]);

  /** Add event note */
  const handleAddNote = useCallback(async (eventId, noteText) => {
    setSaving(true);
    const noteItems = [{
      event_id: eventId,
      campus_id: campusId,
      note: noteText,
      modified_by: getUserId(),
    }];

    const result = await addEventNote(noteItems, getSessionParams());
    setSaving(false);

    if (result && result[0] === true) {
      showToast('success', 'Note added successfully.');
      setNoteDialogOpen(false);
      setNoteEventIndex(null);
      fetchReportingCycle();
    } else {
      showToast('error', `Error adding note${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, getSessionParams, showToast, fetchReportingCycle]);

  /** Start debug logging via MQTT (Inovonics only) */
  const handleStartLogging = useCallback(async (duration) => {
    const payload = [{ manufacture_uuid: serialNum, duration: parseInt(duration) }];
    const result = await startDeviceLogging(campusId, getSessionId(), payload);

    if (result && result[0]) {
      showToast('success', 'Logging has begun. Refresh later to see newest logs.');
    } else {
      showToast('error', 'Error starting logging. Check server and contact support.');
    }
  }, [campusId, serialNum, showToast]);

  /** Send remote command — Find Device (Palcare only) */
  const handleFindDevice = useCallback(async () => {
    const result = await sendRemoteCommand(campusId, getUserId(), getSessionId(), serialNum, 'location');
    if (result) {
      showToast('success', 'Find device sent. Allow several minutes to locate.');
      return getUTCTimestamp();
    } else {
      showToast('error', 'Error sending find command.');
      return null;
    }
  }, [campusId, serialNum, showToast]);

  /** Send remote command — Alarm Device (Palcare only) */
  const handleAlarmDevice = useCallback(async () => {
    const result = await sendRemoteCommand(campusId, getUserId(), getSessionId(), serialNum, 'panic');
    if (result) {
      showToast('success', 'Alarm sent. Allow up to 30 seconds for activation.');
      return getUTCTimestamp();
    } else {
      showToast('error', 'Error sending alarm command.');
      return null;
    }
  }, [campusId, serialNum, showToast]);

  /** Poll device location (Palcare only — called by FindMyDeviceTab) */
  const handlePollLocation = useCallback(async (lastDate) => {
    return fetchDeviceLocation(campusId, serialNum, lastDate);
  }, [campusId, serialNum]);

  /** Open note dialog for a specific event */
  const openNoteDialog = useCallback((eventIndex) => {
    setNoteEventIndex(eventIndex);
    setNoteDialogOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  /* Three states, not two. A suppressed device is still active and reporting, so it
     keeps the green treatment — but saying "Active" while it cannot raise an alert
     asserts something false, which is the one thing a status badge must not do.
     showsMuted() already returns false for an inactive device, so the order here
     only decides the label, not the truth. */
  const showMuted = showsMuted(isActive, awayUntil);
  const statusDot = !isActive ? 'offline' : (showMuted ? 'muted' : 'online');
  const statusLabel = !isActive ? 'Unassigned' : (showMuted ? MUTED_BADGE.label : 'Active');
  const statusTitle = showMuted ? MUTED_BADGE.title : undefined;

  const bannerMeta = useMemo(() => [
    { label: 'Device Type', value: deviceTypeName },
    { label: 'Category', value: deviceCategory },
    { label: isAreaDevice ? 'Area' : 'Assigned To', value: isAreaDevice ? areaName : residentName },
  ], [deviceTypeName, deviceCategory, isAreaDevice, areaName, residentName]);

  // --------------------------------------------------------------------------
  // TAB LIST (conditional by device type)
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => {
    const t = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'reporting', label: 'Reporting' },
      { key: 'wellness', label: 'Wellness' },
    ];
    if (showAdminTabs) {
      t.push({ key: 'logs', label: 'Device Logs' });
    }
    if (isInovonics && showAdminTabs) {
      t.push({ key: 'debug', label: 'Troubleshoot' });
    }
    if (isPalcare) {
      t.push({ key: 'find', label: 'Find My Device' });
    }
    return t;
  }, [showAdminTabs, isInovonics, isPalcare]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/community/devices" className="dp-breadcrumb__text">Devices</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{deviceName || 'Device Profile'}</span>
              </nav>
              <div className="dp-campus-row">
                <label htmlFor="ip-campus-selector" className="dp-campus-label">Campus:</label>
                {campusList.length > 1 ? (
                  <select
                    id="ip-campus-selector"
                    className="dp-campus-select"
                    value={campusId}
                    onChange={handleCampusChange}
                  >
                    {campusList.map((c) => (
                      <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="dp-campus-name">{campusName}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={deviceName ? `${deviceName}${serialNum ? ` (${serialNum})` : ''}` : ''}
          subtitle={modelName}
          statusDot={statusDot}
          statusLabel={statusLabel}
          statusTitle={statusTitle}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {/* Active alerts first. If a device somehow shows both an alarm and a
              suppression, that is exactly the state staff need to see, and the
              alarm is the more urgent of the two. */}
          <ActiveAlertPanel alerts={deviceAlerts} tzOffset={tzOffset} scope="device" />

          {/* Suppression banner — page-level rather than inside BasicInfoTab so a
              muted device cannot hide behind a tab switch. Danger tone because it
              states something true right now: this device is not alerting anyone.

              Deliberately NOT gated on isInovonics, unlike the Mute control: if
              away_until is set on a Palcare device by any route, this page should
              still say so rather than hide a live suppression. */}
          {deviceMuted ? (
            <div className="dpp-mute-banner">
              <i className="fe fe-bell-off dpp-mute-banner__icon" aria-hidden="true" />
              <div className="dpp-mute-banner__text">
                <p className="dpp-mute-banner__title">
                  Alarms suppressed until {convertIsoUTCToLocal(awayUntil, tzOffset)}
                </p>
                <p className="dpp-mute-banner__detail">
                  This device will not produce any alarms until then. Suppression ends on its own.
                </p>
              </div>
              {/* Secondary, not danger: the red matched the old red banner, and a
                  saturated red button on amber reads as a warning about the button
                  itself. Resuming alarms is restorative anyway — the banner carries
                  the attention, this just acts on it. */}
              {editRights ? (
                <button
                  type="button"
                  className="dp-btn dp-btn--secondary dp-btn--sm"
                  onClick={() => handleMuteChange(null)}
                  disabled={muting}
                  title="Resume alarms from this device now"
                >
                  {muting ? 'Resuming…' : 'Stop suppressing'}
                </button>
              ) : null}
            </div>
          ) : null}
          {activeTab === 'basic' && (
            <BasicInfoTab
              basicData={basicData}
              deviceName={deviceName}
              description={description}
              serialNum={serialNum}
              modelName={modelName}
              deviceTypeName={deviceTypeName}
              deviceCategory={deviceCategory}
              behaviorText={behaviorText}
              isAreaDevice={isAreaDevice}
              areaId={areaId}
              areaName={areaName}
              residentId={residentId}
              residentName={residentName}
              lastRepeater={lastRepeater}
              lastAlarm={lastAlarm}
              lastActive={lastActive}
              deviceTypeId={deviceTypeId}
              currentTemp={currentTemp}
              currentHumidity={currentHumidity}
              currentBattery={currentBattery}
              deviceImg={deviceImg}
              isActive={isActive}
              loading={loading}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
              onUnassign={() => setUnassignDialogOpen(true)}
              isPalcare={isPalcare}
              dspProfileName={dspProfileName}
              eventData={eventData}
              campusLat={campusLat}
              campusLong={campusLong}
              tzOffset={tzOffset}
              deviceMuted={deviceMuted}
              showMuted={showMuted}
              alertActive={alertActive}
              onOpenMute={() => setMuteDialogOpen(true)}
            />
          )}

          {activeTab === 'reporting' && (
            <ReportingTab
              hourData={hourData}
              weekData={weekData}
              reasonData={reasonData}
              eventData={eventData}
              eventNotesActive={eventNotesActive}
              isReasonEditable={isReasonEditable}
              isGrandstream={isGrandstream}
              tzOffset={tzOffset}
              loading={reportLoading}
              onAddNote={openNoteDialog}
              deviceId={deviceId}
              deviceName={deviceName}
            />
          )}

          {activeTab === 'wellness' && (
            <WellnessTab
              careData={careData}
              deviceId={deviceId}
              areaGroups={areaGroups}
              resGroups={resGroups}
            />
          )}

          {activeTab === 'logs' && showAdminTabs && (
            <DeviceLogsTab
              logData={logData}
              tzOffset={tzOffset}
            />
          )}

          {activeTab === 'debug' && isInovonics && showAdminTabs && (
            <TroubleshootTab
              debugData={debugData}
              serialNum={serialNum}
              tzOffset={tzOffset}
              onStartLogging={handleStartLogging}
              showToast={showToast}
            />
          )}

          {activeTab === 'find' && isPalcare && (
            <FindMyDeviceTab
              serialNum={serialNum}
              campusLat={campusLat}
              campusLong={campusLong}
              onFindDevice={handleFindDevice}
              onAlarmDevice={handleAlarmDevice}
              onPollLocation={handlePollLocation}
              tzOffset={tzOffset}
            />
          )}
        </div>

        {/* ---- Drawers ---- */}

        {/* Basic Info Drawer */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          description={description}
          serialNum={serialNum}
          deviceUseId={deviceUseId}
          isAreaDevice={isAreaDevice}
          areaId={areaId}
          residentId={residentId}
          areas={areas}
          residents={residents}
          useMapData={useMapData}
          isPalcare={isPalcare}
          dspProfileId={dspProfileId}
          dspProfiles={dspProfiles}
        />

        {/* Unassign confirmation — requires typing "confirm". Wording comes from
            the shared device copy module so this reads identically to unassigning
            the same device from the Devices list. */}
        {unassignDialogOpen ? (() => {
          const copy = describeDeviceAction('profile-unassign');
          return (
            <ConfirmTypedDialog
              title={copy.title}
              confirmLabel={copy.confirmLabel}
              savingLabel={copy.savingLabel}
              onClose={() => setUnassignDialogOpen(false)}
              onConfirm={handleUnassign}
              saving={saving}
            >
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                Are you sure you want to unassign{' '}
                <strong>{deviceName || 'this device'}</strong>?
              </p>
              {/* The operational warning — deliberately not muted. */}
              <p style={{ margin: 'var(--space-3) 0 0', lineHeight: 1.6 }}>
                {copy.consequence}
              </p>
              <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                {copy.closing}
              </p>
            </ConfirmTypedDialog>
          );
        })() : null}

        {/* Suppress Alarms dialog — opened from the Status & Sensors header.
          Resuming is not routed through here; that is one click on the banner. */}
      {muteDialogOpen ? (
        <MuteDeviceDialog
          deviceName={deviceName}
          tzOffset={tzOffset}
          onClose={() => setMuteDialogOpen(false)}
          onConfirm={handleMuteChange}
          saving={muting}
        />
      ) : null}

      {/* Event Note Dialog */}
        {noteDialogOpen && noteEventIndex !== null && (
          <EventNoteDialog
            event={eventData[noteEventIndex]}
            prenoteData={prenoteData}
            eventNotesPreform={eventNotesPreform}
            eventNotesFreeform={eventNotesFreeform}
            tzOffset={tzOffset}
            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
            onSave={handleAddNote}
            saving={saving}
          />
        )}
      </div>
      </div>
    </>
  );
};

export default DeviceProfilePage;
